import React, { useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, TextField, InputAdornment, Paper, ClickAwayListener } from '@mui/material';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import SearchIcon from '@mui/icons-material/Search';
import { SectionTab } from './tab-button';
import { t } from '../config';

// ─── Color conversion utilities ──────────────────────────────────────

function hsvToRgb(h, s, v) {
  const i = Math.floor(h / 60) % 6;
  const f = h / 60 - Math.floor(h / 60);
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const tt = v * (1 - (1 - f) * s);
  let r, g, b;
  switch (i) {
    case 0: r = v; g = tt; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = tt; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = tt; g = p; b = v; break;
    default: r = v; g = p; b = q; break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0, s = max === 0 ? 0 : d / max, v = max;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6 * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return [h, s, v];
}

function hexToRgb(hex) {
  hex = (hex || '000000').replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  if (hex.length !== 6) return [0, 0, 0];
  return [parseInt(hex.substr(0, 2), 16) || 0, parseInt(hex.substr(2, 2), 16) || 0, parseInt(hex.substr(4, 2), 16) || 0];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
}

function hueToColor(h) { return rgbToHex(...hsvToRgb(h, 1, 1)); }

// ─── Constants ───────────────────────────────────────────────────────

const UNSPLASH_KEY = 'zwiE802FyRjTmLhZjXqW';
const UNSPLASH_BASE = 'https://api.polotno.com/api/get-unsplash';

const SWATCH_COLORS = ['#ffffff', '#1a237e', '#ff6d00', '#2e7d32', '#fdd835', '#9c27b0'];

const PICKER_PRESETS = [
  '#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3', '#9c27b0',
  '#00bcd4', '#8bc34a', '#cddc39', '#000000', '#607d8b', '#ffffff',
];

const CHECKER_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Crect width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' width='8' height='8' fill='%23fff'/%3E%3Crect y='8' width='8' height='8' fill='%23fff'/%3E%3C/svg%3E";

// ─── Saturation / Brightness Square ─────────────────────────────────

const SatBrightSquare = ({ hue, sat, bright, onChange, size = 220 }) => {
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    onChange(x, 1 - y);
  }, [onChange]);

  useEffect(() => {
    const onMove = (e) => { if (dragging.current) { e.preventDefault(); update(e); } };
    const onUp = () => { dragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [update]);

  return (
    <Box
      ref={ref}
      onMouseDown={(e) => { dragging.current = true; update(e); }}
      sx={{
        width: size, height: Math.round(size * 0.7), position: 'relative', cursor: 'crosshair',
        borderRadius: '4px', userSelect: 'none',
        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueToColor(hue)})`,
      }}
    >
      <Box sx={{
        position: 'absolute', left: `${sat * 100}%`, top: `${(1 - bright) * 100}%`,
        width: 12, height: 12, borderRadius: '50%',
        border: '2px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.6)',
        transform: 'translate(-50%, -50%)', pointerEvents: 'none',
      }} />
    </Box>
  );
};

// ─── Hue Bar ─────────────────────────────────────────────────────────

const HueBar = ({ hue, onChange, width = 220 }) => {
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    onChange(Math.max(0, Math.min(360, ((e.clientX - rect.left) / rect.width) * 360)));
  }, [onChange]);

  useEffect(() => {
    const onMove = (e) => { if (dragging.current) { e.preventDefault(); update(e); } };
    const onUp = () => { dragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [update]);

  return (
    <Box
      ref={ref}
      onMouseDown={(e) => { dragging.current = true; update(e); }}
      sx={{
        width, height: 14, borderRadius: '7px', position: 'relative', cursor: 'pointer',
        background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
        userSelect: 'none', mt: 1,
      }}
    >
      <Box sx={{
        position: 'absolute', left: `${(hue / 360) * 100}%`, top: '50%',
        width: 14, height: 14, borderRadius: '50%',
        border: '2px solid #fff', boxShadow: '0 0 2px rgba(0,0,0,0.5)',
        transform: 'translate(-50%, -50%)', pointerEvents: 'none',
        backgroundColor: hueToColor(hue),
      }} />
    </Box>
  );
};

// ─── Color Picker (used inside Solid & Linear tabs) ──────────────────

const ColorPicker = ({ color, onChange }) => {
  const [hsv, setHsv] = useState(() => rgbToHsv(...hexToRgb(color)));
  const [rgb, setRgb] = useState(() => hexToRgb(color));
  const [hexInput, setHexInput] = useState((color || '#000000').replace('#', ''));

  useEffect(() => {
    const r = hexToRgb(color || '#000000');
    const h = rgbToHsv(...r);
    setRgb(r);
    setHsv(h);
    setHexInput((color || '#000000').replace('#', ''));
  }, [color]);

  const emit = (h, s, v) => {
    const newRgb = hsvToRgb(h, s, v);
    const hex = rgbToHex(...newRgb);
    setHsv([h, s, v]);
    setRgb(newRgb);
    setHexInput(hex.replace('#', ''));
    onChange(hex);
  };

  const handleHex = (val) => {
    setHexInput(val);
    if (/^[0-9a-fA-F]{6}$/.test(val)) {
      const r = hexToRgb(val);
      setRgb(r);
      setHsv(rgbToHsv(...r));
      onChange('#' + val);
    }
  };

  const handleRgb = (idx, val) => {
    const v = parseInt(val);
    if (isNaN(v) || v < 0 || v > 255) return;
    const newRgb = [...rgb]; newRgb[idx] = v;
    setRgb(newRgb);
    setHsv(rgbToHsv(...newRgb));
    const hex = rgbToHex(...newRgb);
    setHexInput(hex.replace('#', ''));
    onChange(hex);
  };

  const smallSx = { width: 38, '& .MuiInputBase-input': { fontSize: 11, py: 0.3, px: 0.3, textAlign: 'center' } };

  return (
    <Box>
      <SatBrightSquare hue={hsv[0]} sat={hsv[1]} bright={hsv[2]} onChange={(s, v) => emit(hsv[0], s, v)} />
      <HueBar hue={hsv[0]} onChange={(h) => emit(h, hsv[1], hsv[2])} />

      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, alignItems: 'center' }}>
        <TextField size="small" value={hexInput} onChange={(e) => handleHex(e.target.value)}
          onFocus={(e) => e.target.select()}
          sx={{ width: 60, '& .MuiInputBase-input': { fontSize: 11, py: 0.3, px: 0.5 } }} />
        {[0, 1, 2].map((i) => (
          <TextField key={i} size="small" type="number" value={rgb[i]}
            onChange={(e) => handleRgb(i, e.target.value)} onFocus={(e) => e.target.select()} sx={smallSx} inputProps={{ min: 0, max: 255 }} />
        ))}
        <TextField size="small" type="number" value={100} disabled sx={smallSx} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
        {['Hex', 'R', 'G', 'B', 'A'].map((label, i) => (
          <Typography key={label} variant="caption" sx={{ width: i === 0 ? 60 : 38, textAlign: 'center', color: '#888', fontSize: 10 }}>
            {label}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {PICKER_PRESETS.map((c) => (
          <Box key={c} onClick={() => onChange(c)} sx={{
            width: 18, height: 18, borderRadius: '3px', backgroundColor: c,
            border: '1px solid #ddd', cursor: 'pointer',
            '&:hover': { transform: 'scale(1.2)' }, transition: 'transform 0.1s',
          }} />
        ))}
      </Box>
    </Box>
  );
};

// ─── Color Popup (Solid / Linear tabs) ───────────────────────────────

const ColorPopup = ({ open, onClose, background, onBackgroundChange }) => {
  const [tab, setTab] = useState('solid');
  const [solidColor, setSolidColor] = useState('#000000');
  const [gradColors, setGradColors] = useState(['#000000', '#ffffff']);
  const [gradAngle, setGradAngle] = useState(0);
  const [activeStop, setActiveStop] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (background && background.startsWith('{')) {
      try {
        const g = JSON.parse(background);
        if (g.type === 'linear') {
          setTab('linear');
          setGradColors(g.colors || ['#000000', '#ffffff']);
          setGradAngle(g.angle || 0);
          return;
        }
      } catch (e) {}
    }
    setTab('solid');
    if (background && background.startsWith('#')) setSolidColor(background);
  }, [open]);

  const handleSolid = (c) => { setSolidColor(c); onBackgroundChange(c); };

  const handleGradColor = (c) => {
    const nc = [...gradColors]; nc[activeStop] = c; setGradColors(nc);
    onBackgroundChange(JSON.stringify({ type: 'linear', colors: nc, angle: gradAngle }));
  };

  const handleAngle = (a) => {
    setGradAngle(a);
    onBackgroundChange(JSON.stringify({ type: 'linear', colors: gradColors, angle: a }));
  };

  if (!open) return null;

  const tabStyle = (active) => ({
    cursor: 'pointer', fontWeight: active ? 600 : 400,
    color: active ? '#1976d2' : '#666',
    borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
    pb: 0.3, px: 0.5, userSelect: 'none',
  });

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper elevation={4} sx={{ position: 'absolute', top: 52, left: 0, zIndex: 1000, p: 1.5, width: 252, borderRadius: '8px' }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, borderBottom: '1px solid #e0e0e0', pb: 0.5 }}>
          <Typography variant="body2" onClick={() => setTab('solid')} sx={tabStyle(tab === 'solid')}>Solid</Typography>
          <Typography variant="body2" onClick={() => {
            setTab('linear');
            onBackgroundChange(JSON.stringify({ type: 'linear', colors: gradColors, angle: gradAngle }));
          }} sx={tabStyle(tab === 'linear')}>Linear</Typography>
        </Box>

        {tab === 'solid' && <ColorPicker color={solidColor} onChange={handleSolid} />}

        {tab === 'linear' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {gradColors.map((c, i) => (
                <Box key={i} onClick={() => setActiveStop(i)} sx={{
                  width: 32, height: 32, borderRadius: '4px', backgroundColor: c,
                  border: activeStop === i ? '2px solid #1976d2' : '2px solid #ccc', cursor: 'pointer',
                }} />
              ))}
            </Box>

            <Box sx={{
              width: '100%', height: 12, borderRadius: '6px', mb: 1,
              background: `linear-gradient(${gradAngle}deg, ${gradColors[0]}, ${gradColors[1]})`,
              border: '1px solid #e0e0e0',
            }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', border: '1px solid #ccc',
                position: 'relative', flexShrink: 0,
              }}>
                <Box sx={{
                  width: 1, height: 10, backgroundColor: '#333',
                  transformOrigin: 'bottom center', transform: `rotate(${gradAngle}deg)`,
                  position: 'absolute', left: '50%', bottom: '50%', ml: '-0.5px',
                }} />
              </Box>
              <input type="range" min={0} max={360} value={gradAngle}
                onChange={(e) => handleAngle(parseInt(e.target.value))}
                style={{ flex: 1, cursor: 'pointer' }} />
              <Typography variant="caption" sx={{ width: 32, textAlign: 'right', flexShrink: 0 }}>{gradAngle}°</Typography>
            </Box>

            <ColorPicker color={gradColors[activeStop]} onChange={handleGradColor} />
          </>
        )}
      </Paper>
    </ClickAwayListener>
  );
};

// ─── Main Background Panel ───────────────────────────────────────────

const BackgroundPanelContent = observer(({ store }) => {
  const page = store.activePage;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSolidColor, setLastSolidColor] = useState('#ffffff');
  const [hasMore, setHasMore] = useState(true);
  const [pageNum, setPageNum] = useState(1);

  const hasMoreRef = useRef(true);
  const pageNumRef = useRef(1);
  const currentQueryRef = useRef('gradient');
  const debounceRef = useRef(null);
  const isFirstRender = useRef(true);
  const requestIdRef = useRef(0);
  const observerRef = useRef(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    const bg = page?.background || '';
    if (bg && bg.startsWith('#')) setLastSolidColor(bg);
  }, [page?.background]);

  const fetchPhotos = useCallback(async (query, pg = 1) => {
    // For pagination (pg > 1), skip if already fetching to avoid duplicates
    if (pg > 1 && fetchingRef.current) return;
    const id = ++requestIdRef.current;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const q = query || 'gradient';
      const res = await fetch(`${UNSPLASH_BASE}?query=${encodeURIComponent(q)}&per_page=20&page=${pg}&KEY=${UNSPLASH_KEY}`);
      const data = await res.json();
      if (requestIdRef.current !== id) return; // Stale response, ignore
      const results = data.results || (Array.isArray(data) ? data : []);
      const totalPages = data.total_pages || 0;
      if (pg === 1) { setPhotos(results); } else { setPhotos(prev => [...prev, ...results]); }
      const more = pg < totalPages;
      setHasMore(more); hasMoreRef.current = more;
      setPageNum(pg); pageNumRef.current = pg;
      currentQueryRef.current = q;
    } catch (e) {
      if (requestIdRef.current !== id) return;
      console.warn('Unsplash fetch failed:', e);
    }
    fetchingRef.current = false;
    setLoading(false);
  }, []);

  // Initial load
  useEffect(() => { fetchPhotos('gradient', 1); }, [fetchPhotos]);

  // Debounced auto-search (500ms)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const q = searchQuery.trim() || 'gradient';
      hasMoreRef.current = true; pageNumRef.current = 1;
      setHasMore(true); setPageNum(1);
      fetchPhotos(q, 1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, fetchPhotos]);

  // Callback ref for infinite scroll sentinel — reconnects observer on every mount/remount
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!node) return;
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !fetchingRef.current && hasMoreRef.current) {
        fetchPhotos(currentQueryRef.current, pageNumRef.current + 1);
      }
    }, { threshold: 0.1 });
    observerRef.current.observe(node);
  }, [fetchPhotos]);

  const setBackground = useCallback((value) => {
    if (!page) return;
    page.set({ background: value });
    store.history.addUndoState();
  }, [page, store]);

  const bg = page?.background || '';
  const isGradient = bg.startsWith('{');
  const isImage = bg.startsWith('http');
  const displayColor = isGradient || isImage ? lastSolidColor : (bg || '#ffffff');

  return (
    <Box sx={{ p: 1.5, position: 'relative' }}>
      {/* ── Color Swatches Row ── */}
      <Box sx={{ display: 'flex', gap: '6px', mb: 1.5, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'hidden', '&::-webkit-scrollbar': { display: 'none' } }}>
        {/* Palette / color picker trigger */}
        <Box onClick={() => setPickerOpen(!pickerOpen)} sx={{
          width: 30, height: 30, minWidth: 30, borderRadius: '50%', cursor: 'pointer',
          border: pickerOpen ? '2px solid #1976d2' : '2px solid #e0e0e0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'conic-gradient(#f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
          '&:hover': { borderColor: '#1976d2' }, transition: 'border-color 0.15s',
        }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff' }} />
        </Box>

        {/* Preset color swatches */}
        {SWATCH_COLORS.map((c) => (
          <Box key={c} onClick={() => { setBackground(c); setPickerOpen(false); }} sx={{
            width: 30, height: 30, minWidth: 30, borderRadius: '50%', backgroundColor: c, cursor: 'pointer',
            border: bg === c ? '2px solid #1976d2' : '2px solid #e0e0e0',
            '&:hover': { transform: 'scale(1.1)' }, transition: 'all 0.15s',
          }} />
        ))}

        {/* Transparent / remove background */}
        <Box onClick={() => { setBackground('transparent'); setPickerOpen(false); }} sx={{
          width: 30, height: 30, minWidth: 30, borderRadius: '50%', cursor: 'pointer',
          backgroundImage: `url("${CHECKER_SVG}")`, backgroundSize: '8px 8px',
          border: bg === 'transparent' ? '2px solid #1976d2' : '2px solid #e0e0e0',
          '&:hover': { transform: 'scale(1.1)' }, transition: 'all 0.15s',
        }} />
      </Box>

      {/* ── Color Picker Popup ── */}
      <ColorPopup open={pickerOpen} onClose={() => setPickerOpen(false)} background={bg} onBackgroundChange={setBackground} />

      {/* ── Search Input ── */}
      <TextField
        size="small" fullWidth
        placeholder={t('sidePanel.search', 'Search...')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: 13, py: 0.8 } }}
      />

      {/* ── Unsplash Attribution ── */}
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, color: '#888' }}>
        Photos by{' '}
        <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
          Unsplash
        </a>
      </Typography>

      {/* ── Photo Grid ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {photos.map((photo, idx) => {
          const thumb = photo.urls?.small || photo.urls?.thumb || photo.src;
          if (!thumb) return null;
          return (
            <Box key={photo.id || idx} onClick={() => {
              const url = photo.urls?.regular || photo.urls?.small || photo.src;
              if (url) setBackground(url);
            }} sx={{
              cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', aspectRatio: '4/3',
              '&:hover': { opacity: 0.85 }, transition: 'opacity 0.15s',
            }}>
              <img src={thumb} alt={photo.alt_description || 'Background'}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
            </Box>
          );
        })}
      </Box>

      {/* Sentinel for infinite scroll */}
      {hasMore && <Box ref={sentinelRef} sx={{ height: 20 }} />}

      {loading && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#888' }}>
          Loading...
        </Typography>
      )}
      {!loading && photos.length === 0 && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#888' }}>
          No photos found
        </Typography>
      )}
    </Box>
  );
});

export const BackgroundSection = {
  name: 'background',
  Tab: (props) => (
    <SectionTab name={t('sidePanel.background', 'Background')} {...props}>
      <WallpaperIcon />
    </SectionTab>
  ),
  Panel: BackgroundPanelContent,
};
